import {
  useMemo,
  useState,
  useEffect,
  useContext,
  createContext,
  ReactElement,
  useRef,
} from "react";
import { ASCLEPEION_HOSTNAME } from "env-vars";
import { LocationResult } from "../admin-content/types";

export type LocationContextType = {
  isLoading: boolean;
  location: LocationResult | null;
};

const LocationContext = createContext<LocationContextType | undefined>(
  undefined
);

type Props = {
  children: ReactElement;
};

export function LocationProvider({ children }: Props) {
  const [isLoading, setLoading] = useState(true);
  const [locationData, setLocationData] = useState<LocationResult | null>(null);
  const hasFetched = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const locationId = params.get("location_id") || null;
    if (!locationId || hasFetched.current) {
      setLoading(false);
      return;
    }

    const fetchLocation = async () => {
      try {
        const response = await fetch(
          `${ASCLEPEION_HOSTNAME}/v2/locations/${locationId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          return;
        }

        const res = await response.json();
        setLocationData(res);
      } catch (error) {
        console.error("Error fetching location: ", error);
      } finally {
        setLoading(false);
      }
    };

    fetchLocation();
  }, []);

  const contextProps = useMemo(
    () => ({
      isLoading,
      location: locationData,
    }),
    [isLoading, locationData]
  );

  return (
    <LocationContext.Provider value={contextProps}>
      {children}
    </LocationContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export const useLocationContext = () => {
  const context = useContext(LocationContext);

  if (!context) {
    return null;
  }

  return context;
};
